import {
	RadioGroup as HeadlessRadioGroup,
	type RadioGroupProps as HeadlessRadioGroupProps,
	Radio,
	type RadioOptionProps,
} from "@headlessui/react";
import { type ReactNode, type Ref } from "react";
import { useLabelledBy } from "../lib/useLabelledBy";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

// The design system specifies a single Medium size: a 32px pill track (24px segment + 4px inset).
const TRACK_CLASSES = "inline-flex w-fit items-center gap-1 rounded-full bg-surface-neutral p-1";

const SEGMENT_CLASSES =
	"flex items-center justify-center whitespace-nowrap rounded-full px-3 py-0.5 text-sm leading-[20px] font-normal text-secondary cursor-pointer select-none transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-0 focus-visible:ring-brand-accent focus-visible:ring-opacity-30 data-[checked]:bg-surface-base data-[checked]:font-semibold data-[checked]:text-primary data-[checked]:shadow data-[disabled]:cursor-default data-[disabled]:text-subtle data-[disabled]:hover:text-subtle";

type RootProps<TValue> = Omit<HeadlessRadioGroupProps<"div", TValue>, "className"> & {
	className?: string;
	"aria-labelledby"?: string;

	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;
	ref?: Ref<HTMLDivElement>;
};

const Root = <TValue,>({
	trackingId,
	className,
	ref,
	"aria-labelledby": ariaLabelledBy,
	...rest
}: RootProps<TValue>) => {
	const rootRef = useLabelledBy<HTMLDivElement>(ariaLabelledBy, ref);
	return (
		<HeadlessRadioGroup
			ref={rootRef}
			className={cn(TRACK_CLASSES, className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		/>
	);
};

type SegmentProps<TValue> = Omit<RadioOptionProps<"div", TValue>, "className" | "children"> & {
	children?: ReactNode;
	className?: string;
	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;
	ref?: Ref<HTMLDivElement>;
};

const Segment = <TValue,>({
	trackingId,
	className,
	children,
	ref,
	...rest
}: SegmentProps<TValue>) => (
	<Radio
		ref={ref}
		className={cn(SEGMENT_CLASSES, className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{children}
	</Radio>
);

export const SegmentedControls = Object.assign(Root, { Segment });

export type SegmentedControlsProps<TValue> = RootProps<TValue>;
export type SegmentedControlsSegmentProps<TValue> = SegmentProps<TValue>;
